import React, { useCallback, useEffect, useMemo, } from 'react';
import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import { AtlasForm, IForm, IFormOptions, } from 'atlas-form';
import {
  PhonenumberSetupCostParentEditForm,
  PhonenumberSetupCostParentEditInitialValues,
  PhonenumberSetupCostParentComponents,
  PhonenumberSetupCostParentAdditionalParams,
  PhonenumberSetupCostParentFormOptions,
  onPhonenumberSetupCostParentFormSaved,
  IPhonenumberSetupCostParentFormOptionsProps,
  AlertError,
} from '@Utils';
import { ISharedPhonenumberSetupCostParent, ISharedPatchPhonenumberSetupCostParent, PermissionEnum, CrudEnum, } from 'atlas-shared';
import { fetchPhonenumberSetupCostParent, patchPhonenumberSetupCostParent, getPhonenumberSetupCostParentUpdateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withEdit, IWithEditProps, } from '@Hocs/with.edit';
import { useAbilities, useAuth, useOrganizations, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithEditProps<ISharedPhonenumberSetupCostParent> {}

const PhonenumberSetupCostParentFormEdit = ({ id, value, setValue, formSchema, schemaLoading, saving, save, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const abilities = useAbilities();
  const dispatch = useAppDispatch();
  const organizations = useOrganizations();

  useEffect(() => {
    fetchPhonenumberSetupCostParent(id).then(phonenumber_setup_cost_parent => setValue(phonenumber_setup_cost_parent));
  }, [id, setValue, ]);

  const param_props: IPhonenumberSetupCostParentFormOptionsProps = {
    t,
    is_new: false,
    abilities,
    organizations: organizations.organizations,
  };
  const formLayout: IForm | undefined = useMemo(() => value && PhonenumberSetupCostParentEditForm(value, param_props, t), [value, organizations, t, abilities, ]);
  const options: IFormOptions | undefined = useMemo(() => value && PhonenumberSetupCostParentFormOptions(value, param_props, t), [value, organizations, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedPatchPhonenumberSetupCostParent, form) => {
    onChange?.(values, changedValue, form);
  }, []);
  const onFinish = useCallback((values: ISharedPatchPhonenumberSetupCostParent) => value && save(() => patchPhonenumberSetupCostParent(value.id, values).then(
    res => onSaved ? onSaved(res, navigate) : onPhonenumberSetupCostParentFormSaved(t, 'edit', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [value, save, navigate, t, ]);
  
  if (schemaLoading)
    return <AppSpinner />;

  if (!value)
    return <>{t('ITEM_NOT_FOUND')}</>;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !value || abilities.can(null, CrudEnum.Update, PermissionEnum.PhonenumberSetupCostParent, value)}
    >
      <AtlasForm<ISharedPatchPhonenumberSetupCostParent>
        dispatch={dispatch}
        formLayout={formLayout as IForm}
        form={formSchema}
        options={options}
        components={PhonenumberSetupCostParentComponents}
        additionalParams={PhonenumberSetupCostParentAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={PhonenumberSetupCostParentEditInitialValues(value)}
        saving={saving}
        title={title ? t(title) : `${t('EDIT')} ${t('PHONENUMBER_SETUP_COST_PARENT')}`}
        entityId={value.id}
        entity={value}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const PhonenumberSetupCostParentFormEditView = React.memo(withEdit(PhonenumberSetupCostParentFormEdit, getPhonenumberSetupCostParentUpdateSchema));
